import React, { useState } from "react";

import { ReactComponent as SearchIcon } from "../../assets/icons/search-sm.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/x-close.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter-lines.svg";

import { STICKY_CLASSES } from "../ReusableComponents/FilterBar";
import Select from "../ReusableComponents/Select";

export interface ToolbarSortOption {
  value: string;
  label: string;
}

interface ModelsToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  /** Wires the label to the field, so it must be unique on the page. */
  searchId?: string;
  availableChips: string[];
  selectedChips: string[];
  onChipToggle: (chip: string) => void;
  countLabel: string;
  sortBy: string;
  onSortChange: (value: string) => void;
  sortOptions: ToolbarSortOption[];
  sortAriaLabel?: string;
  /** Optional single-choice status filter, rendered as a Select before the sort; pass all three together. First option is the "no filter" value. */
  statusValue?: string;
  onStatusChange?: (value: string) => void;
  statusOptions?: ToolbarSortOption[];
  // Outer spacing only — the card itself is always eai-panel.
  className?: string;
  /** Pins the card to the top of the page scroll so results run under it. Page-only — inside a modal the negative gutters below belong to the page shell, not the modal body. */
  sticky?: boolean;
  // Render the row while data is still loading, inert so there's nothing to filter against yet.
  disabled?: boolean;
  /** Phone-only controls shown at the top of the Filters panel — the page's own seg row, hidden below sm. */
  mobileControls?: React.ReactNode;
  /** How many of mobileControls are off their default, added to the Filters badge. */
  mobileActiveCount?: number;
  /** Folds the count behind the Filters toggle on a phone too, so the card matches Home's single row. */
  collapseCountOnMobile?: boolean;
}

// Search + chips + count + sort card, shared by the Models grid, the Model Garden and the template picker. Same anatomy as FilterBar on Home and the group page — a eai-panel holding a labelled search field with the secondary controls beside it — so every filter row in the app reads as one component. The difference is category chips: they multi-select, which a Select can't express, so they get their own line under the field.
const ModelsToolbar: React.FC<ModelsToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search",
  searchId = "models-toolbar-search",
  availableChips,
  selectedChips,
  onChipToggle,
  countLabel,
  sortBy,
  onSortChange,
  sortOptions,
  sortAriaLabel = "Sort",
  statusValue,
  onStatusChange,
  statusOptions,
  className = "",
  sticky = false,
  disabled = false,
  mobileControls,
  mobileActiveCount = 0,
  collapseCountOnMobile = false,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const hasStatus = Boolean(statusOptions?.length && onStatusChange);
  // A non-default status is a hidden filter on a phone too, so the badge counts it
  const statusActive = hasStatus && statusValue !== statusOptions?.[0]?.value;
  const activeCount = selectedChips.length + (statusActive ? 1 : 0) + mobileActiveCount;
  const hideChipRow = collapseCountOnMobile && !filtersOpen;

  return (
    <div className={sticky ? `${STICKY_CLASSES} ${className}` : className}>
      <div className="eai-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1 basis-48">
            <label htmlFor={searchId} className="eai-meta-label mb-1.5 block">
              Search
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <SearchIcon className="h-4 w-4 text-eai-ink-faint" />
              </div>
              <input
                id={searchId}
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                disabled={disabled}
                className="eai-input pl-10 pr-10"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  aria-label="Clear search"
                  className="group absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <div className="rounded-full p-1 transition-colors group-hover:bg-eai-surface-hover group-active:bg-eai-surface-active">
                    <CloseIcon className="h-4 w-4 text-eai-ink-faint group-hover:text-eai-ink [&_path]:stroke-current" />
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Phone-only opener for the categories and the sort; the badge keeps hidden filters visible */}
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls={`${searchId}-filters`}
            data-testid="models-toolbar-toggle"
            className="eai-btn-outline shrink-0 px-4 sm:hidden"
          >
            <FilterIcon className="h-4 w-4 shrink-0 [&_path]:stroke-current" />
            Filters
            {activeCount > 0 && (
              <span className="ml-0.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-eai-cta-primary px-1.5 font-outfit text-eai-caption text-white">
                {activeCount}
              </span>
            )}
          </button>

          {/* sm:contents dissolves this wrapper above the breakpoint, so the sort rejoins the row above as an ordinary flex item rather than sitting in a nested box */}
          <div
            id={`${searchId}-filters`}
            className={`w-full flex-col gap-3 ${filtersOpen ? "flex" : "hidden"} sm:contents`}
          >
            {mobileControls && <div className="flex flex-col gap-3 sm:hidden">{mobileControls}</div>}

            {hasStatus && (
              <div className="w-full sm:w-44">
                <Select
                  id={`${searchId}-status`}
                  label="Status"
                  value={statusValue}
                  onChange={(e) => onStatusChange?.(e.target.value)}
                  aria-label="Filter by status"
                  disabled={disabled}
                >
                  {statusOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div className="w-full sm:w-48">
              <Select
                id={`${searchId}-sort`}
                label="Sort"
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                aria-label={sortAriaLabel}
                disabled={disabled}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Categories multi-select, so they stay chips rather than becoming a second Select. The count stays visible on a phone unless collapseCountOnMobile folds it in with the chips. */}
        {(availableChips.length > 0 || countLabel) && (
          <div className={`mt-3 ${hideChipRow ? "hidden" : "flex"} flex-wrap items-center gap-2 sm:flex`}>
            <div
              className={`${filtersOpen ? "flex" : "hidden"} min-w-0 flex-wrap gap-2 sm:flex`}
            >
              {availableChips.map((chip) => {
                const isSelected = selectedChips.includes(chip);
                return (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => onChipToggle(chip)}
                    aria-pressed={isSelected}
                    className={`eai-chip ${isSelected ? "eai-chip-active" : ""}`}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
            {countLabel && (
              <span className="ml-auto whitespace-nowrap font-outfit text-eai-body-xs tracking-[0.01em] text-eai-ink-subtle">
                {countLabel}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelsToolbar;
